import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { dashboardLinks } from "@/components/lib/SidebarLinks";
import { Card, CardContent } from "@/components/ui/card";
import { fetchOverviewReport } from "@/redux/slice/dashboard/dashboard-slice";
import { restaurant_id } from "@/api/lib/url";
import {
  Archive,
  BadgePoundSterling,
  BarChart3,
  BookOpen,
  Briefcase,
  Calendar,
  CalendarCheck,
  CalendarDays,
  CalendarX,
  CheckCircle2,
  CircleDollarSign,
  Clock,
  Coins,
  CreditCard,
  Gift,
  Globe2,
  Landmark,
  Layers,
  Menu,
  Percent,
  Phone,
  PhoneCall,
  PhoneIncoming,
  RotateCcw,
  ShoppingBag,
  Star,
  Tag,
  Tags,
  Ticket,
  TrendingUp,
  Truck,
  UserCheck,
  UserRound,
  Users,
  Utensils,
  UtensilsCrossed,
  XCircle,
} from "lucide-react";

const gradientStyles = {
  call: "linear-gradient(90deg, #06b6d4 0%, #10b981 100%)",
  customer: "linear-gradient(90deg, #3b82f6 0%, #6366f1 100%)",
  order: "linear-gradient(90deg, #f59e0b 0%, #f97316 100%)",
  revenue: "linear-gradient(90deg, #10b981 0%, #059669 100%)",
  average: "linear-gradient(90deg, #0284c7 0%, #06b6d4 100%)",
  refund: "linear-gradient(90deg, #f43f5e 0%, #e11d48 100%)",
  loyalty: "linear-gradient(90deg, #a855f7 0%, #c084fc 100%)",
  loyaltyValue: "linear-gradient(90deg, #7c3aed 0%, #a855f7 100%)",
  points: "linear-gradient(90deg, #ec4899 0%, #f43f5e 100%)",
  offers: "linear-gradient(90deg, #ef4444 0%, #f97316 100%)",
  offerValue: "linear-gradient(90deg, #22c55e 0%, #10b981 100%)",
  staff: "linear-gradient(90deg, #14b8a6 0%, #06b6d4 100%)",
  reservation: "linear-gradient(90deg, #4f46e5 0%, #3b82f6 100%)",
  menu: "linear-gradient(90deg, #ea580c 0%, #f59e0b 100%)",
  miscellaneous: "linear-gradient(90deg, #64748b 0%, #94a3b8 100%)",
};

const solidColors = {
  call: "#06b6d4",
  customer: "#3b82f6",
  order: "#f59e0b",
  revenue: "#10b981",
  average: "#0284c7",
  refund: "#f43f5e",
  loyalty: "#a855f7",
  loyaltyValue: "#7c3aed",
  points: "#ec4899",
  offers: "#ef4444",
  offerValue: "#22c55e",
  staff: "#14b8a6",
  reservation: "#4f46e5",
  menu: "#ea580c",
  miscellaneous: "#94a3b8",
};

const sectionHeaderIcons = {
  call: Phone,
  customer: Users,
  order: ShoppingBag,
  revenue: BadgePoundSterling,
  average: BarChart3,
  refund: RotateCcw,
  loyalty: Gift,
  loyaltyValue: Coins,
  points: Star,
  offers: Tag,
  offerValue: Percent,
  staff: UserCheck,
  reservation: CalendarDays,
  menu: UtensilsCrossed,
  miscellaneous: Layers,
};

function buildSections(data) {
  if (!data) return [];

  const fmt = (val) => (val != null ? val.toLocaleString() : "0");
  const fmtCurrency = (val) => `£ ${val != null ? val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00"}`;

  return [
    {
      title: "Call Log",
      border: "call",
      items: [
        { label: "Total Calls", value: fmt(data.call_log?.total_calls), icon: Phone },
        { label: "Pickup Calls", value: fmt(data.call_log?.pickup_calls), icon: PhoneIncoming },
        { label: "Delivery Calls", value: fmt(data.call_log?.delivery_calls), icon: Truck },
        { label: "Reservation Calls", value: fmt(data.call_log?.reservation_calls), icon: Calendar },
      ],
    },
    {
      title: "Customer",
      border: "customer",
      items: [
        { label: "Total Customer", value: fmt(data.customers?.total_customers), icon: Users },
        { label: "Pickup Customer", value: fmt(data.customers?.pickup_customers), icon: PhoneCall },
        { label: "In-House Customer", value: fmt(data.customers?.in_house_customers), icon: Landmark },
        { label: "Delivery Customer", value: fmt(data.customers?.delivery_customers), icon: Truck },
        { label: "Online Customer", value: fmt(data.customers?.online_customers), icon: Globe2 },
        { label: "Table Customer", value: fmt(data.customers?.table_customers), icon: Utensils },
      ],
    },
    {
      title: "Orders",
      border: "order",
      items: [
        { label: "Total Order", value: fmt(data.orders?.total_orders), icon: Users },
        { label: "Pickup Order", value: fmt(data.orders?.pickup_orders), icon: PhoneCall },
        { label: "In-House Order", value: fmt(data.orders?.in_house_orders), icon: Landmark },
        { label: "Delivery Order", value: fmt(data.orders?.delivery_orders), icon: Truck },
        { label: "Online Order", value: fmt(data.orders?.online_orders), icon: Globe2 },
        { label: "Table Order", value: fmt(data.orders?.table_orders), icon: Utensils },
      ],
    },
    {
      title: "Revenue (Total)",
      border: "revenue",
      items: [
        { label: "Total Revenue", value: fmtCurrency(data.revenue_total?.total_revenue), icon: BadgePoundSterling },
        { label: "Pickup Revenue", value: fmtCurrency(data.revenue_total?.pickup_revenue), icon: PhoneCall },
        { label: "In-House Revenue", value: fmtCurrency(data.revenue_total?.in_house_revenue), icon: Landmark },
        { label: "Delivery Revenue", value: fmtCurrency(data.revenue_total?.delivery_revenue), icon: Truck },
        { label: "Online Revenue", value: fmtCurrency(data.revenue_total?.online_revenue), icon: Globe2 },
        { label: "Table Revenue", value: fmtCurrency(data.revenue_total?.table_revenue), icon: Utensils },
      ],
    },
    {
      title: "Revenue (Average)",
      border: "average",
      items: [
        { label: "Pick-Up Revenue", value: fmtCurrency(data.revenue_average?.pickup_revenue), icon: PhoneCall },
        { label: "In-House Revenue", value: fmtCurrency(data.revenue_average?.in_house_revenue), icon: Landmark },
        { label: "Delivery Revenue", value: fmtCurrency(data.revenue_average?.delivery_revenue), icon: Truck },
        { label: "Online Revenue", value: fmtCurrency(data.revenue_average?.online_revenue), icon: Globe2 },
        { label: "Table Revenue", value: fmtCurrency(data.revenue_average?.table_revenue), icon: Utensils },
      ],
    },
    {
      title: "Refund",
      border: "refund",
      items: [
        { label: "Total Refund", value: fmt(data.refund?.total_refund), icon: BadgePoundSterling },
        { label: "Pickup", value: fmt(data.refund?.pickup_refund), icon: PhoneCall },
        { label: "In-House", value: fmt(data.refund?.in_house_refund), icon: Landmark },
        { label: "Delivery", value: fmt(data.refund?.delivery_refund), icon: Truck },
        { label: "Online", value: fmt(data.refund?.online_refund), icon: Globe2 },
      ],
    },
    {
      title: "Loyalty",
      border: "loyalty",
      items: [
        { label: "Total Gift Card", value: fmt(data.loyalty?.total_gift_cards), icon: Gift },
        { label: "Gift Card", value: fmt(data.loyalty?.gift_cards), icon: Gift },
        { label: "C2C Gift Card", value: fmt(data.loyalty?.c2c_gift_cards), icon: Tag },
        { label: "Voucher", value: fmt(data.loyalty?.vouchers), icon: Ticket },
      ],
    },
    {
      title: "Loyalty Value",
      border: "loyaltyValue",
      items: [
        { label: "Total Value", value: fmtCurrency(data.loyalty_value?.total_value), icon: Gift },
        { label: "Gift Card", value: fmtCurrency(data.loyalty_value?.gift_card_value), icon: Gift },
        { label: "C2C Gift Card", value: fmtCurrency(data.loyalty_value?.c2c_gift_card_value), icon: Tag },
        { label: "Voucher", value: fmtCurrency(data.loyalty_value?.voucher_value), icon: Ticket },
      ],
    },
    {
      title: "Loyalty Points",
      border: "points",
      items: [
        { label: "Total used points", value: fmt(data.loyalty_points?.total_used_points), icon: Gift },
        { label: "Total used points value", value: fmtCurrency(data.loyalty_points?.total_used_points_value), icon: Gift },
      ],
    },
    {
      title: "Offers",
      border: "offers",
      items: [
        { label: "Total Offers", value: fmt(data.offers?.total_offers), icon: Percent },
        { label: "Buy Get Free", value: fmt(data.offers?.buy_get_free), icon: Percent },
        { label: "Buy Get Discount", value: fmt(data.offers?.buy_get_discount), icon: Percent },
        { label: "Spend Get Free", value: fmt(data.offers?.spend_get_free), icon: Percent },
        { label: "Spend Get Discount", value: fmt(data.offers?.spend_get_discount), icon: Percent },
        { label: "Online Discount", value: fmt(data.offers?.online_discount), icon: Percent },
      ],
    },
    {
      title: "Offer Value",
      border: "offerValue",
      items: [
        { label: "Total Value", value: fmtCurrency(data.offer_value?.total_value), icon: BadgePoundSterling },
        { label: "Buy Get Free Value", value: fmtCurrency(data.offer_value?.buy_get_free_value), icon: BadgePoundSterling },
        { label: "Buy Get Discount Value", value: fmtCurrency(data.offer_value?.buy_get_discount_value), icon: BadgePoundSterling },
        { label: "Spend Get Free Value", value: fmtCurrency(data.offer_value?.spend_get_free_value), icon: BadgePoundSterling },
        { label: "Spend Get Discount Value", value: fmtCurrency(data.offer_value?.spend_get_discount_value), icon: BadgePoundSterling },
        { label: "Online Discount Value", value: fmtCurrency(data.offer_value?.online_discount_value), icon: BadgePoundSterling },
      ],
    },
    {
      title: "Staff",
      border: "staff",
      items: [
        { label: "Total Active Staff", value: fmt(data.staff?.total_active_staff), icon: UserRound },
        { label: "Full Time", value: fmt(data.staff?.full_time), icon: Briefcase },
        { label: "Part Time", value: fmt(data.staff?.part_time), icon: Clock },
      ],
    },
    {
      title: "Reservation",
      border: "reservation",
      items: [
        { label: "Total Reservation", value: fmt(data.reservation?.total_reservations), icon: CalendarCheck },
        { label: "Total Table", value: fmt(data.reservation?.total_tables), icon: Calendar },
        { label: "Total Guest", value: fmt(data.reservation?.total_guests), icon: Users },
        { label: "Total Canceled", value: fmt(data.reservation?.total_cancelled), icon: CalendarX },
      ],
    },
    {
      title: "Menu",
      border: "menu",
      items: [
        { label: "Total Publish Menu", value: fmt(data.menu?.total_publish_menu), icon: BookOpen },
        { label: "Active Menu", value: fmt(data.menu?.active_menu), icon: CheckCircle2 },
        { label: "Inactive Menu", value: fmt(data.menu?.inactive_menu), icon: XCircle },
        { label: "Archived Menu", value: fmt(data.menu?.archived_menu), icon: Archive },
      ],
    },
    {
      title: "Miscellaneous ( Value )",
      border: "miscellaneous",
      items: [
        { label: "Total Compensation", value: fmtCurrency(data.miscellaneous?.total_compensation), icon: CircleDollarSign },
        { label: "Total Complimentary", value: fmtCurrency(data.miscellaneous?.total_complimentary), icon: CircleDollarSign },
        { label: "Total Replaced", value: fmtCurrency(data.miscellaneous?.total_replaced), icon: CircleDollarSign },
      ],
    },
  ];
}

function OverviewCard({ item, borderType }) {
  const Icon = item.icon;
  const gradient = gradientStyles[borderType];
  const solidColor = solidColors[borderType];

  return (
    <div className="group relative rounded-xl border border-white/[0.08] bg-[#14161b] p-4 transition-all duration-300 hover:border-white/20 hover:bg-[#181b22] hover:shadow-lg hover:shadow-black/40 overflow-hidden select-none">
      {/* Top 2px Accent Gradient Line */}
      <div 
        className="absolute top-0 left-0 right-0 h-[2px] opacity-70 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: gradient }}
      />

      <div className="flex items-start justify-between gap-3">
        {/* Label */}
        <p className="text-[11px] font-semibold text-gray-400 group-hover:text-gray-200 transition-colors uppercase tracking-wider leading-snug">
          {item.label}
        </p>

        {/* Icon Container */}
        <div 
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/5 bg-white/[0.03] transition-all duration-300 group-hover:bg-white/[0.08] group-hover:scale-105"
          style={{ color: solidColor }}
        >
          <Icon size={18} strokeWidth={2} className="transition-transform duration-300 group-hover:rotate-6" />
        </div>
      </div>

      {/* Value */}
      <p className="text-2xl font-extrabold text-white tracking-tight leading-none mt-2">
        {item.value}
      </p>
    </div>
  );
}

function OverviewSection({ section }) {
  const HeaderIcon = sectionHeaderIcons[section.border] || Layers;
  const solidColor = solidColors[section.border];

  return (
    <section className="space-y-4">
      {/* Left-Aligned Header Bar */}
      <div className="flex items-center gap-3">
        <div 
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/5 bg-white/[0.04]"
          style={{ color: solidColor }}
        >
          <HeaderIcon size={18} strokeWidth={2} />
        </div>
        <h3 className="text-base font-bold text-white tracking-tight">
          {section.title}
        </h3>
        <span className="px-2.5 py-0.5 rounded-full bg-white/[0.04] border border-white/5 text-[11px] font-medium text-gray-400">
          {section.items.length} metrics
        </span>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {section.items.map((item) => (
          <OverviewCard
            key={`${section.title}-${item.label}`}
            item={item}
            borderType={section.border}
          />
        ))}
      </div>
    </section>
  );
}

function DashboardOverview() {
  const dispatch = useDispatch();
  const { overviewReport, overviewLoading } = useSelector(
    (state) => state.dashboard,
  );

  useEffect(() => {
    dispatch(fetchOverviewReport({ restaurant_id }));
  }, [dispatch]);

  const sections = buildSections(overviewReport);

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={dashboardLinks} />}
      pageName={{ title: "Overview" }}
      paths={[
        { name: "Dashboard", path: "/dashboard/overview" },
        { name: "Overview", path: "/dashboard/overview" },
      ]}
    >
      <div className="container mx-auto space-y-8 px-6 pb-12 pt-4 text-cwhite">
        
        {/* Insights Period Banner */}
        {overviewReport && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-5 rounded-xl bg-[#14161b] border border-white/[0.08] shadow-lg">
            <div>
              <h2 className="text-lg font-bold text-white tracking-tight">Overview Insights</h2>
              <p className="text-xs text-gray-400 mt-0.5 font-medium">Real-time operational summary across all restaurant channels</p>
            </div>
            {overviewReport.start_date && overviewReport.end_date && (
              <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08] text-xs font-semibold text-cyellow w-fit shadow-inner">
                <span className="text-gray-400 font-normal">Report Period:</span>
                <span className="text-white font-medium">{overviewReport.start_date}</span>
                <span className="text-gray-500 font-normal">to</span>
                <span className="text-white font-medium">{overviewReport.end_date}</span>
              </div>
            )}
          </div>
        )}

        {overviewLoading && sections.length === 0 && (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-cblack7 border-t-cyellow" />
          </div>
        )}
        {!overviewLoading && sections.length === 0 && (
          <div className="flex items-center justify-center py-20 text-gray-400">
            No overview data available.
          </div>
        )}
        {sections.map((section) => (
          <OverviewSection key={section.title} section={section} />
        ))}
      </div>
    </Layout2>
  );
}

export default DashboardOverview;
